/**
 * TechnicianLayout Component
 *
 * Mobile-first layout for the Technician Portal.
 * Includes compact header at top, main content area, and fixed bottom navigation.
 * Optimized for touch interactions and field work usage.
 *
 * @module components/technician/layout/TechnicianLayout
 */

import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import TechnicianHeader from './TechnicianHeader';
import TechnicianBottomNav from './TechnicianBottomNav';
import TechnicianMoreMenu from './TechnicianMoreMenu';
import './TechnicianLayout.css';

const TechnicianLayout = () => {
  const { user, profile, isAuthenticated, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [, setNotifications] = useState(0);

  // Close more menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreMenuOpen && !event.target.closest('.more-menu-container')) {
        setMoreMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [moreMenuOpen]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (moreMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [moreMenuOpen]);

  useEffect(() => {
    setUnreadMessages(0);
    setNotifications(0);
  }, []);

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="technician-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect non-technicians to their appropriate dashboard
  if (profile?.role !== 'technician') {
    if (profile?.role === 'admin') {
      return <Navigate to="/dashboard" replace />;
    }
    if (profile?.role === 'client') {
      return <Navigate to="/progreso/galeria" replace />;
    }
    return <Navigate to="/login" replace />;
  }

  const handleLogout = async () => {
    setMoreMenuOpen(false);
    await signOut();
    navigate('/login');
  };

  const handleMoreMenuToggle = () => {
    setMoreMenuOpen(!moreMenuOpen);
  };

  const handleMoreMenuClose = () => {
    setMoreMenuOpen(false);
  };

  const handleNavigation = (path) => {
    setMoreMenuOpen(false);
    navigate(path);
  };

  return (
    <div className="technician-layout">
      {/* Compact Header */}
      <TechnicianHeader
        userName={profile?.full_name || user?.email}
        onLogout={handleLogout}
      />

      {/* Main Content Area */}
      <main className="technician-content">
        <Outlet />
      </main>

      {/* Bottom Navigation */}
      <TechnicianBottomNav
        onMoreClick={handleMoreMenuToggle}
        moreMenuOpen={moreMenuOpen}
      />

      {/* More Menu Overlay */}
      {moreMenuOpen && (
        <div className="more-menu-overlay" onClick={handleMoreMenuClose}>
          <div className="more-menu-container" onClick={(e) => e.stopPropagation()}>
            <TechnicianMoreMenu
              onClose={handleMoreMenuClose}
              onNavigate={handleNavigation}
              onLogout={handleLogout}
              unreadMessages={unreadMessages}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TechnicianLayout;
